import { takeLatest, put, select, call } from 'redux-saga/effects';
import * as Sentry from '@sentry/browser';
import * as types from '../constants/ActionTypes';
import {
  unauthorized,
  accessDenied,
  serverError,
  defaultError,
  sessionExpired,
} from '../constants/NetworkErrors';
import { deleteToken } from '../functions/tokenUtils';
import { clearHijackSession, storeLoginUrl } from '../functions/authV1';

export function* updateError(action) {
  try {
    const {
      error,
      error: { status },
    } = action;

    const errorsState = yield select((state) => state.network.errors);

    if (status === 401) {
      // TODO: add sentry
      if (errorsState.includes(unauthorized)) return;
      yield put({ type: types.NETWORK_UNAUTHORIZED });

      /**
       * Ensure we don't get stuck in endless "Logging in" state.
       * For any unauthorized API response, drop auth token and redirect to login.
       * `requestUser` saga already does similar logic, but 401 can happen on any endpoint
       * before user profile is requested (e.g. /api/v1/quests).
       */
      try {
        const pathname = window.location?.pathname || '';
        if (!pathname.includes('/login') && !pathname.includes('/logout')) {
          // Show a user-friendly message before redirect
          yield put({ type: types.CUSTOM_ERROR, error: sessionExpired });
          yield call(() => new Promise((resolve) => setTimeout(resolve, 1200)));

          clearHijackSession();
          deleteToken();
          const nextPath = `${pathname}${window.location?.search || ''}${
            window.location?.hash || ''
          }`;
          yield put({
            type: types.LOGOUT,
            email: '',
            isHardRedirect: true,
            returnUrl: storeLoginUrl(nextPath),
          });
        }
      } catch (e) {
        // ignore redirect failures, the unauthorized error is still recorded
      }
    } else if (status === 403) {
      if (errorsState.includes(accessDenied)) return;
      yield put({ type: types.NETWORK_ACCESS_DENIED });
    } else if (status === 409) {
      yield put({ type: types.NETWORK_CONFLICT });
    } else if (status === 500) {
      if (errorsState.includes(serverError)) return;
      yield put({ type: types.NETWORK_SERVER_ERROR });
    } else if (status !== 400 && status !== 404) {
      if (errorsState.includes(defaultError)) return;
      yield put({ type: types.NETWORK_DEFAULT_ERROR });
    } else if (status === 400) {
      if (errorsState.includes(unauthorized)) return;
      // TODO: handle messages
      yield put({ type: types.NETWORK_BAD_REQUEST });
    }

    if (status !== 400 && status !== 404) {
      Sentry.withScope((scope) => {
        scope.setExtra('error', error);
        scope.setExtra('cookies', document.cookie);
        scope.setLevel('error');
        // No HTTP status means a JS exception thrown inside a saga (e.g. a TypeError
        // while handling a successful response) — report it with its own stack trace
        // instead of lumping everything into "Network error. Status: undefined".
        if (status === undefined && error instanceof Error) {
          Sentry.captureException(error);
        } else {
          Sentry.captureMessage(`Network error. Status: ${status}`);
        }
      });
    }
  } catch (error) {
    yield put({ type: types.RESET_NETWORK_ERRORS });
  }
}

export default function* watchNetwork() {
  yield takeLatest(types.NETWORK_ERROR, updateError);
}
